import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { colors } from "utils";
import { Tooltip } from "components/shared/Tooltip/Tooltip";
import { ChevronDownIcon, InfoIcon } from "components/pages/WarmUpPicker/icons";
import { isQuestionCount } from "components/pages/WarmUpPicker/warmUpSets";
import { Message, SelectFrame, VisuallyHidden, WHITE, interText, nativeSelect } from "components/pages/WarmUpPicker/styles";
import { SESSION_LENGTH_OPTIONS } from "./AssignmentAccordion";
import { CheckIcon, CopyIcon } from "./warmUpCardIcons";
import { WarmUpGroupMenu } from "./WarmUpGroupMenu";
import { insideStroke } from "./cardStroke";
import {
  narrowLeftColumn,
  narrowMiddleColumn,
  narrowRightColumn,
  narrowRow,
  narrowSessionRow,
  useNarrow,
} from "./wrappingCardLayout";

// Warm-Up Group card (Figma 6463:7962): one per warm-up assignment of the class.

// Figma colors with no token in `colors` (the token object is not edited).
const SEPARATOR_GRAY = "#CBCDCC";

const ERROR_ORANGE = "#BA4A21";

export const kebabId = (assignmentId) => `warm-up-card-actions-${assignmentId}`;
export const setLinkId = (assignmentId) => `warm-up-card-set-link-${assignmentId}`;

const SESSION_TIP = "How long each warm-up session runs. A change applies to sessions that start after it.";

const setLinkText = ({ question_count: count }) =>
  isQuestionCount(count) ? `Warm-Up Challenge Set (${count})` : "Warm-Up Challenge Set";

const COPIED_FEEDBACK_MS = 2000;

// The code whose link was last copied, for 2000 ms after the clipboard write
// succeeds; a failed write confirms nothing.
const useCopiedCode = (onCopyCode) => {
  const [copiedCode, setCopiedCode] = useState(null);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = (code) =>
    onCopyCode(code)
      .then(() => {
        clearTimeout(timer.current);
        setCopiedCode(code);
        timer.current = setTimeout(() => setCopiedCode(null), COPIED_FEEDBACK_MS);
      })
      .catch(() => {});

  return { copiedCode, copy };
};

// The five standard lengths, preceded by the stored one when it is not among
// them ("30 Minutes"), so the select never shows a length that is not stored.
const sessionOptions = (stored) =>
  SESSION_LENGTH_OPTIONS.some(({ value }) => value === stored)
    ? SESSION_LENGTH_OPTIONS
    : [{ value: stored, label: `${stored} Minutes` }, ...SESSION_LENGTH_OPTIONS];

// Optimistic: the picked length shows at once and the select is locked until
// the save answers; a failure puts the stored length back.
const useSessionLength = (stored, onSessionLengthChange) => {
  const [pending, setPending] = useState(null);
  const [failed, setFailed] = useState(false);

  const change = (value) => {
    if (pending !== null || value === stored) return;
    setFailed(false);
    setPending(value);
    onSessionLengthChange(value)
      .then(() => setPending(null))
      .catch(() => {
        setPending(null);
        setFailed(true);
      });
  };

  return { value: pending ?? stored, saving: pending !== null, failed, change };
};

const formatDate = (iso) => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString("en-US");
};

export const WarmUpGroupCard = ({
  assignment,
  onCopyCode,
  menuOpen,
  onMenuOpenChange,
  onCopyToClasses,
  onEdit,
  onArchive,
  archiveFailed = false,
  onSessionLengthChange,
  onOpenSet,
}) => {
  const { id, warmup_set: set, groups } = assignment;
  const updated = formatDate(assignment.last_updated);
  const { copiedCode, copy } = useCopiedCode(onCopyCode);
  const sessionLength = useSessionLength(assignment.session_length, onSessionLengthChange);
  const titleId = `warm-up-card-title-${id}`;
  const selectId = `warm-up-card-session-${id}`;
  const tipId = `warm-up-card-session-tip-${id}`;
  const cardRef = useRef(null);
  const sessionRowRef = useRef(null);
  const narrow = useNarrow(cardRef, sessionRowRef);
  return (
    <Card ref={cardRef} aria-labelledby={titleId}>
      {archiveFailed && <ArchiveErrorNote role="alert">Your warm-up groups were not archived. Try again.</ArchiveErrorNote>}
      <ContentRow $narrow={narrow}>
        <LeftColumn $narrow={narrow}>
          <Tag>Warm-Up Groups</Tag>
          <HeaderBlock>
            <SetName id={titleId}>{set.name}</SetName>
            <MetaRow>
              {updated && (
                <DateItem>
                  <span>{`Last updated: ${updated}`}</span>
                  {" "}
                  <Separator aria-hidden="true">|</Separator>
                </DateItem>
              )}
              <SetLink type="button" id={setLinkId(id)} onClick={onOpenSet}>
                {setLinkText(set)}
              </SetLink>
            </MetaRow>
          </HeaderBlock>
        </LeftColumn>
        <MiddleColumn $narrow={narrow}>
          <GroupCodeLabel>Group Code:</GroupCodeLabel>
          {groups.length === 0 ? (
            <Message>No group codes</Message>
          ) : (
            <CodeList>
              {groups.map((group) => {
                const code = group.code.toUpperCase();
                const copied = code === copiedCode;
                return (
                  <CodeItem key={group.id}>
                    <Code>{code}</Code>
                    <IconButton
                      type="button"
                      aria-label={`Copy share link for group code ${code}`}
                      title={copied ? "Copied!" : "Copy group code link"}
                      onClick={() => copy(code)}
                    >
                      {copied ? <CheckIcon /> : <CopyIcon />}
                    </IconButton>
                  </CodeItem>
                );
              })}
            </CodeList>
          )}
          <SessionRow ref={sessionRowRef} $narrow={narrow}>
            <SessionLabelGroup>
              <Tooltip id={tipId} tip={SESSION_TIP}>
                <InfoButton type="button" aria-label="About session length" aria-describedby={tipId}>
                  <InfoIcon stroke={colors.nightlyWoods500} />
                </InfoButton>
              </Tooltip>
              <SessionLabel htmlFor={selectId}>Session Length:</SessionLabel>
            </SessionLabelGroup>
            <SelectFrame>
              <SessionSelect
                id={selectId}
                value={sessionLength.value}
                disabled={sessionLength.saving}
                aria-busy={sessionLength.saving ? "true" : undefined}
                onChange={(event) => sessionLength.change(Number(event.target.value))}
              >
                {sessionOptions(assignment.session_length).map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </SessionSelect>
              <ChevronDownIcon stroke={colors.blue500} />
            </SelectFrame>
          </SessionRow>
          {sessionLength.failed && <ErrorNote role="alert">Session length was not saved. Try again.</ErrorNote>}
        </MiddleColumn>
        <RightColumn $narrow={narrow}>
          <WarmUpGroupMenu
            buttonId={kebabId(id)}
            setName={set.name}
            isOpen={menuOpen}
            onOpenChange={onMenuOpenChange}
            onCopyToClasses={onCopyToClasses}
            onEdit={onEdit}
            onArchive={onArchive}
          />
        </RightColumn>
      </ContentRow>
      <VisuallyHidden role="status">{copiedCode ? `Link for ${copiedCode} copied` : ""}</VisuallyHidden>
    </Card>
  );
};

// No overflow clipping: the tag needs none and the card menu must not be cut.
const Card = styled.article`
  background: ${WHITE};
  border: none;
  border-radius: 12px;
  box-shadow: 0 1px 2px 0 rgba(14, 26, 49, 0.05);
  box-sizing: border-box;
  padding: 16px 24px 16px 0;
  position: relative;
  width: 100%;
  ${insideStroke(12)}
`;

const ErrorNote = styled.p`
  ${interText(14, 20, 400, "0")}
  color: ${ERROR_ORANGE};
  margin: 0;
`;

// Above the content row, in line with the set name (the card has no left padding).
const ArchiveErrorNote = styled(ErrorNote)`
  margin: 0 0 8px 24px;
`;

const ContentRow = styled.div`
  align-items: stretch;
  display: flex;
  gap: 24px;
  ${(p) => p.$narrow && narrowRow}
`;

const LeftColumn = styled.div`
  display: flex;
  flex: 0 0 482px;
  flex-direction: column;
  gap: 8px;
  ${(p) => p.$narrow && narrowLeftColumn}
`;

// Only this column shrinks on narrower windows.
const MiddleColumn = styled.div`
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  gap: 16px;
  justify-content: center;
  min-width: 0;
  ${(p) => p.$narrow && narrowMiddleColumn}
`;

const RightColumn = styled.div`
  align-items: flex-end;
  display: flex;
  flex: 0 0 96px;
  flex-direction: column;
  ${(p) => p.$narrow && narrowRightColumn}
`;

// Flush with the card's left edge, a pill on the right only. Figma's 136px;
// Chrome's Inter metrics would leave it at 135.2.
const Tag = styled.span`
  ${interText(14, 20, 700, "0")}
  align-self: flex-start;
  background: ${colors.blue800};
  border-radius: 0 200px 200px 0;
  box-sizing: border-box;
  color: ${WHITE};
  padding: 2px 8px;
  white-space: nowrap;
  width: 136px;
`;

const HeaderBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 24px;
`;

const SetName = styled.h3`
  ${interText(18, 26, 700)}
  margin: 0;
  max-width: 428px;
`;

const MetaRow = styled.div`
  ${interText(14, 20, 400, "0")}
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

// Figma sets the "|" one space after the date; the row's gap comes only
// before the link.
const DateItem = styled.span`
  white-space: nowrap;
`;

const Separator = styled.span`
  color: ${SEPARATOR_GRAY};
`;

const SetLink = styled.button`
  ${interText(14, 20, 400, "0")}
  background: transparent;
  border: none;
  color: ${colors.blue700};
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
`;

const GroupCodeLabel = styled.div`
  ${interText(16, 22, 700)}
  color: ${colors.nightlyWoods950};
  margin: 0;
`;

const CodeList = styled.ul`
  column-gap: 24px;
  display: flex;
  flex-wrap: wrap;
  list-style: none;
  margin: 0;
  padding: 0;
  row-gap: 16px;
`;

const CodeItem = styled.li`
  align-items: center;
  display: inline-flex;
  gap: 8px;
`;

const Code = styled.span`
  ${interText(18, 26, 700)}
  color: ${colors.nightlyWoods950};
  white-space: nowrap;
`;

const IconButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  height: 24px;
  padding: 0;
  width: 24px;
`;

const SessionRow = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
  ${(p) => p.$narrow && narrowSessionRow}
`;

const SessionLabelGroup = styled.div`
  align-items: center;
  display: flex;
  gap: 4px;
`;

const InfoButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  padding: 0;
`;

const SessionLabel = styled.label`
  ${interText(14, 20, 700, "0")}
  margin: 0;
  white-space: nowrap;
`;

// Locked while saving, but drawn as usual: full opacity, no "not allowed" cursor.
const SessionSelect = styled.select`
  ${nativeSelect(175, 36)}

  &:disabled {
    color: ${colors.nightlyWoods500};
    cursor: default;
    opacity: 1;
  }
`;
